import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useEffect, useState } from 'react';
import { requireArenaSlug } from '@/utils/arena';
import { arenaService } from '@/services/arenaService';

// Types
export interface KontakProfileValues {
  // Kontak Arena & Social Links (shown in Image 2)
  whatsapp: string;
  email: string;
  instagram: string;
  tiktok: string;
  youtube: string;

  // Penanggung Jawab (preserved functionality)
  namaPenanggungJawab: string;
  jabatan: string;
}

// Initial Values
const kontakInitial: KontakProfileValues = {
  whatsapp: '',
  email: '',
  instagram: '',
  tiktok: '',
  youtube: '',
  namaPenanggungJawab: '',
  jabatan: '',
};

// Validation Schema
const kontakSchema = Yup.object({
  whatsapp: Yup.string().required('Nomor WhatsApp wajib diisi'),
  email: Yup.string().email('Format email tidak valid').required('Email wajib diisi'),
  instagram: Yup.string(),
  tiktok: Yup.string(),
  namaPenanggungJawab: Yup.string(),
  jabatan: Yup.string(),
});

interface KontakTabProps {
  onSubmit: (values: KontakProfileValues, tabId: string) => Promise<void>;
  onSubmittingChange?: (isSubmitting: boolean) => void;
}

const FormikSubmittingWatcher = ({
  isSubmitting,
  onSubmittingChange,
}: {
  isSubmitting: boolean;
  onSubmittingChange?: (isSubmitting: boolean) => void;
}) => {
  useEffect(() => {
    onSubmittingChange?.(isSubmitting);
    return () => {
      onSubmittingChange?.(false);
    };
  }, [isSubmitting, onSubmittingChange]);
  return null;
};

const PenanggungJawabTab = ({ onSubmit, onSubmittingChange }: KontakTabProps) => {
  const [initial, setInitial] = useState<KontakProfileValues>(kontakInitial);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const slug = requireArenaSlug();
        const arena = await arenaService.getArenaBySlug(slug);
        if (!mounted || !arena) return;

        const staff = (arena as any).staff || {};
        const social = arena.social_links || {};

        setInitial({
          whatsapp: social.whatsapp || staff.phone || arena.phone || '',
          email: staff.email || arena.email || '',
          instagram: social.instagram || '',
          tiktok: social.tiktok || '',
          youtube: social.youtube || '',
          namaPenanggungJawab: staff.name || '',
          jabatan: staff.position || '',
        });
      } catch (_err) {
        // ignore
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, []);

  const handleSubmit = async (values: KontakProfileValues) => {
    try {
      const slug = requireArenaSlug();

      // 1. Update Staff
      const staffPayload = {
        name: values.namaPenanggungJawab || values.email.split('@')[0],
        email: values.email,
        phone: values.whatsapp,
        position: values.jabatan || 'Penanggung Jawab',
      };
      await arenaService.updateArenaStaff(slug, staffPayload);

      // 2. Update Arena Social Links
      const existingArena = await arenaService.getArenaBySlug(slug);
      if (existingArena) {
        const arenaPayload = {
          name: existingArena.name || '',
          kode_wilayah: existingArena.kode_wilayah || '00000',
          kecamatan: (existingArena.maps && existingArena.maps.kecamatan) || '',
          address: (existingArena.maps && existingArena.maps.address) || '',
          city: (existingArena.maps && existingArena.maps.city) || '',
          city_slug: (existingArena.maps && (existingArena.maps as any).city_slug) || undefined,
          google_maps_url: (existingArena.maps && existingArena.maps.google_maps_url) || '',
          description: existingArena.description || '',
          sport_categories: existingArena.sport_categories?.categories || [],
          social_links: {
            whatsapp: values.whatsapp || undefined,
            instagram: values.instagram || undefined,
            tiktok: values.tiktok || undefined,
            youtube: values.youtube || undefined,
          },
        };
        await arenaService.updateArena(slug, arenaPayload);
      }

      await onSubmit(values, 'penanggung-jawab');
    } catch (err) {
      console.error('Update kontak & staff failed', err);
      throw err;
    }
  };

  return (
    <Formik
      initialValues={initial}
      enableReinitialize
      validationSchema={kontakSchema}
      onSubmit={async (values, helpers) => {
        try {
          await handleSubmit(values);
        } catch (_err) {
          helpers.setSubmitting(false);
        }
      }}
    >
      {({ values, errors, touched, handleChange, isSubmitting }) => (
        <Form id="active-profile-form" className="space-y-6 max-w-8xl">
          <FormikSubmittingWatcher
            isSubmitting={isSubmitting}
            onSubmittingChange={onSubmittingChange}
          />
          {/* Main Card: Kontak (matches Screenshot 2) */}
          <div className="bg-white rounded-2xl border border-gray-200/80 p-6 shadow-2xs space-y-5">
            <div className="text-[11px] font-semibold text-gray-400 tracking-wider uppercase">
              KONTAK ARENA
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Nomor WhatsApp */}
              <div>
                <label className="text-xs font-semibold text-[#0E1726] mb-1.5 block">
                  Nomor WhatsApp
                </label>
                <input
                  type="text"
                  name="whatsapp"
                  value={values.whatsapp}
                  onChange={handleChange}
                  placeholder="081234567890"
                  className="w-full h-11 px-4 bg-white border border-gray-200 rounded-xl text-xs text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] focus:ring-1 focus:ring-[#FC480D] transition-colors"
                />
                {errors.whatsapp && touched.whatsapp && (
                  <p className="text-red-500 text-[11px] mt-1">{errors.whatsapp}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="text-xs font-semibold text-[#0E1726] mb-1.5 block">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  placeholder="halo@gelorafutsal.id"
                  className="w-full h-11 px-4 bg-white border border-gray-200 rounded-xl text-xs text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] focus:ring-1 focus:ring-[#FC480D] transition-colors"
                />
                {errors.email && touched.email && (
                  <p className="text-red-500 text-[11px] mt-1">{errors.email}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Instagram */}
              <div>
                <label className="text-xs font-semibold text-[#0E1726] mb-1.5 block">
                  Instagram (opsional)
                </label>
                <input
                  type="text"
                  name="instagram"
                  value={values.instagram}
                  onChange={handleChange}
                  placeholder="@gelorafutsal"
                  className="w-full h-11 px-4 bg-white border border-gray-200 rounded-xl text-xs text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] focus:ring-1 focus:ring-[#FC480D] transition-colors"
                />
              </div>

              {/* TikTok */}
              <div>
                <label className="text-xs font-semibold text-[#0E1726] mb-1.5 block">
                  TikTok (opsional)
                </label>
                <input
                  type="text"
                  name="tiktok"
                  value={values.tiktok}
                  onChange={handleChange}
                  placeholder="@gelorafutsal"
                  className="w-full h-11 px-4 bg-white border border-gray-200 rounded-xl text-xs text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] focus:ring-1 focus:ring-[#FC480D] transition-colors"
                />
              </div>
            </div>
          </div>

          {/* Secondary Card: Detail Penanggung Jawab (Preserved functionality) */}
          <div className="bg-white rounded-2xl border border-gray-200/80 p-6 shadow-2xs space-y-5">
            <div className="text-[11px] font-semibold text-gray-400 tracking-wider uppercase">
              DETAIL PENANGGUNG JAWAB
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="text-xs font-semibold text-[#0E1726] mb-1.5 block">
                  Nama Penanggung Jawab
                </label>
                <input
                  type="text"
                  name="namaPenanggungJawab"
                  value={values.namaPenanggungJawab}
                  onChange={handleChange}
                  placeholder="Nama lengkap penanggung jawab"
                  className="w-full h-11 px-4 bg-white border border-gray-200 rounded-xl text-xs text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] focus:ring-1 focus:ring-[#FC480D] transition-colors"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-[#0E1726] mb-1.5 block">
                  Jabatan
                </label>
                <input
                  type="text"
                  name="jabatan"
                  value={values.jabatan}
                  onChange={handleChange}
                  placeholder="Manajer Operasional / Pemilik"
                  className="w-full h-11 px-4 bg-white border border-gray-200 rounded-xl text-xs text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] focus:ring-1 focus:ring-[#FC480D] transition-colors"
                />
              </div>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default PenanggungJawabTab;
